import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetcher } from '@/api/fetcher';
import {
  CreateRenderedSummaryError,
  CreateRenderedSummaryRequest,
  CreateRenderedSummaryResponse,
  PatchRenderedSummaryError,
  PatchRenderedSummaryRequest,
  PatchRenderedSummaryResponse,
  RenderedSummariesError,
  RenderedSummariesRequest,
  RenderedSummariesResponse,
  RenderedSummaryError,
  RenderedSummaryRequest,
  RenderedSummaryResponse,
} from '@/api/model';

const KEY = {
  GET_RENDERED_SUMMARIES: 'GET_RENDERED_SUMMARIES',
  GET_RENDERED_SUMMARY: 'GET_RENDERED_SUMMARY',
} as const;

const request = {
  getRenderedSummary: async ({ payload: { uuid } }: RenderedSummaryRequest) => {
    const { data } = await fetcher.FCT.get<RenderedSummaryResponse>(
      `${ENDPOINTS.FCT.RENDERED_SUMMARY.BASE}${uuid}`,
    );

    return data;
  },
  getReportRenderedSummaries: async ({ payload: { uuid } }: RenderedSummariesRequest) => {
    const { data } = await fetcher.FCT.get<RenderedSummariesResponse>(
      `${ENDPOINTS.FCT.RENDERED_SUMMARY.REPORT}${uuid}`,
    );

    return data;
  },
  patchRenderedSummary: async ({ payload }: PatchRenderedSummaryRequest) => {
    const { data } = await fetcher.FCT.patch<PatchRenderedSummaryResponse>(
      `${ENDPOINTS.FCT.RENDERED_SUMMARY.BASE}${payload.uuid}`,
      payload,
    );

    return data;
  },
  postCreateRenderedSummary: async ({ payload }: CreateRenderedSummaryRequest) => {
    const { data } = await fetcher.FCT.post<CreateRenderedSummaryResponse>(
      ENDPOINTS.FCT.RENDERED_SUMMARY.BASE,
      payload,
    );

    return data;
  },
};

const query = {
  useGetRenderedSummary: (
    { payload }: RenderedSummaryRequest,
    config?: Partial<UseQueryOptions<RenderedSummaryResponse, RenderedSummaryError>>,
  ) =>
    useQuery<RenderedSummaryResponse, RenderedSummaryError>({
      queryFn: () => request.getRenderedSummary({ payload }),
      queryKey: [KEY.GET_RENDERED_SUMMARY, payload.uuid],
      ...config,
    }),
  useGetReportRenderedSummaries: (
    { payload }: RenderedSummariesRequest,
    config?: Partial<UseQueryOptions<RenderedSummariesResponse, RenderedSummariesError>>,
  ) =>
    useQuery<RenderedSummariesResponse, RenderedSummariesError>({
      queryFn: () => request.getReportRenderedSummaries({ payload }),
      queryKey: [KEY.GET_RENDERED_SUMMARIES, payload.uuid],
      ...config,
    }),
};

const mutation = {
  usePatchRenderedSummary: () =>
    useMutation<
      PatchRenderedSummaryResponse,
      PatchRenderedSummaryError,
      PatchRenderedSummaryRequest
    >({
      mutationFn: request.patchRenderedSummary,
    }),
  usePostCreateRenderedSummary: () =>
    useMutation<
      CreateRenderedSummaryResponse,
      CreateRenderedSummaryError,
      CreateRenderedSummaryRequest
    >({
      mutationFn: request.postCreateRenderedSummary,
    }),
};

export const renderedSummary = {
  KEY,
  mutation,
  query,
  request,
};
